import React, { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';


const NotificationSettings = ({ userProfile, onSave, saving }) => {
  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    outage_alerts: true,
    resolution_updates: true,
    weekly_summary: false,
    marketing_emails: false,
    frequency: 'immediate' // immediate, daily, weekly
  });

  useEffect(() => {
    // In a real app, you'd fetch user's current notification settings
    // For now, using defaults
  }, [userProfile]);

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    // In a real implementation, you'd save to a user_notification_preferences table
    onSave({ notification_preferences: settings });
  };

  const NotificationGroup = ({ title, description, children }) => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        {description && (
          <p className="text-gray-600 text-sm mt-1">{description}</p>
        )}
      </div>
      <div className="space-y-3 pl-4">
        {children}
      </div>
    </div>
  );

  const NotificationOption = ({ label, description, checked, onChange, icon: Icon }) => (
    <div className="flex items-start space-x-3">
      <Checkbox
        id={label}
        checked={checked}
        onCheckedChange={onChange}
        className="mt-1"
      />
      <div className="flex-1">
        <div className="flex items-center">
          {Icon && <Icon className="h-4 w-4 text-gray-500 mr-2" />}
          <label htmlFor={label} className="text-sm font-medium text-gray-900 cursor-pointer">
            {label}
          </label>
        </div>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Bell className="h-5 w-5 mr-2" />
          Notification Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Customize how and when you receive notifications about outages and updates
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Delivery Methods */}
        <NotificationGroup
          title="Delivery Methods"
          description="Choose how you want to receive notifications"
        >
          <NotificationOption
            label="Email Notifications"
            description="Receive notifications via email"
            checked={settings?.email_notifications}
            onChange={(checked) => handleSettingChange('email_notifications', checked)}
            icon={Mail}
          />
          
          <NotificationOption
            label="SMS Notifications"
            description="Receive notifications via text message"
            checked={settings?.sms_notifications}
            onChange={(checked) => handleSettingChange('sms_notifications', checked)}
            icon={MessageSquare}
          />
          
          <NotificationOption
            label="Push Notifications"
            description="Receive notifications in your browser"
            checked={settings?.push_notifications}
            onChange={(checked) => handleSettingChange('push_notifications', checked)}
            icon={Bell}
          />
        </NotificationGroup>

        {/* Outage Notifications */}
        <NotificationGroup
          title="Outage Notifications"
          description="Control which outage-related notifications you receive"
        >
          <NotificationOption
            label="New Outage Alerts"
            description="Get notified when new outages are reported for your subscribed providers"
            checked={settings?.outage_alerts}
            onChange={(checked) => handleSettingChange('outage_alerts', checked)}
            icon={Bell}
          />
          
          <NotificationOption
            label="Resolution Updates"
            description="Get notified when outages are resolved"
            checked={settings?.resolution_updates}
            onChange={(checked) => handleSettingChange('resolution_updates', checked)}
            icon={Bell}
          />
        </NotificationGroup>

        {/* Summary Reports */}
        <NotificationGroup
          title="Summary Reports"
          description="Periodic summaries of outage activity"
        >
          <NotificationOption
            label="Weekly Summary"
            description="Receive a weekly summary of outage activity for your subscribed providers"
            checked={settings?.weekly_summary}
            onChange={(checked) => handleSettingChange('weekly_summary', checked)}
            icon={Bell}
          />
        </NotificationGroup>

        {/* Marketing */}
        <NotificationGroup
          title="Marketing & Updates"
          description="News and updates from ServiceWatch"
        >
          <NotificationOption
            label="Product Updates"
            description="Receive emails about new features and improvements"
            checked={settings?.marketing_emails}
            onChange={(checked) => handleSettingChange('marketing_emails', checked)}
            icon={Mail}
          />
        </NotificationGroup>

        {/* Frequency Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Notification Frequency</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="immediate"
                name="frequency"
                value="immediate"
                checked={settings?.frequency === 'immediate'}
                onChange={(e) => handleSettingChange('frequency', e?.target?.value)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="immediate" className="text-sm font-medium text-gray-900">
                Immediate
              </label>
              <span className="text-sm text-gray-600">
                Receive notifications as soon as events occur
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="daily"
                name="frequency"
                value="daily"
                checked={settings?.frequency === 'daily'}
                onChange={(e) => handleSettingChange('frequency', e?.target?.value)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="daily" className="text-sm font-medium text-gray-900">
                Daily Digest
              </label>
              <span className="text-sm text-gray-600">
                Receive a daily summary of all notifications
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="weekly"
                name="frequency"
                value="weekly"
                checked={settings?.frequency === 'weekly'}
                onChange={(e) => handleSettingChange('frequency', e?.target?.value)}
                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="weekly" className="text-sm font-medium text-gray-900">
                Weekly Summary
              </label>
              <span className="text-sm text-gray-600">
                Receive a weekly summary of all notifications
              </span>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NotificationSettings;